import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Clock, Play, Pause, RotateCcw, CheckCircle, Coffee, Plus, Trash2,
  Volume2, VolumeX, ArrowRight, SkipForward, GripVertical, ListTodo,
  Timer, Target, FileText, ChevronDown, ChevronUp,
} from "lucide-react";
import { API } from "../App";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const PomodoroPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);

  // Settings
  const [workDuration, setWorkDuration] = useState(25);
  const [breakDuration, setBreakDuration] = useState(5);
  const [longBreakDuration, setLongBreakDuration] = useState(15);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Planning
  const [tasks, setTasks] = useState([{ task_name: "", objective: "" }]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  // Timer
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [phase, setPhase] = useState("work"); // work | break | long_break
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(null); // session_id being reviewed
  const [reviewText, setReviewText] = useState("");

  const intervalRef = useRef(null);
  const audioContextRef = useRef(null);

  // Max pomodori for 8h
  const maxPomodori = Math.floor((8 * 60) / workDuration);

  useEffect(() => {
    fetchTodayPlan();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const fetchTodayPlan = async () => {
    try {
      const res = await axios.get(`${API}/pomodoro/plan/today`, { withCredentials: true });
      if (res.data) {
        setPlan(res.data);
        const completed = res.data.sessions.filter(s => s.status === "completed").length;
        setCompletedCount(completed);
        // Find next pending task
        const nextIdx = res.data.sessions.findIndex(s => s.status === "pending" || s.status === "deferred");
        setCurrentIndex(nextIdx >= 0 ? nextIdx : res.data.sessions.length);
        setWorkDuration(res.data.work_duration);
        setBreakDuration(res.data.break_duration);
        setLongBreakDuration(res.data.long_break_duration);
        setTimeLeft(res.data.work_duration * 60);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Audio
  const playChime = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      const playNote = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
        gain.gain.setValueAtTime(0, ctx.currentTime + start);
        gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + start + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur);
      };
      playNote(1047, 0, 0.6);
      playNote(1319, 0.3, 0.8);
      playNote(1568, 0.6, 1.0);
    } catch (e) { /* no audio */ }
  }, [soundEnabled]);

  // Timer logic
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    } else if (timeLeft === 0 && isRunning) {
      setIsRunning(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
      playChime();
      if (phase === "work") {
        handleWorkComplete();
      } else {
        handleBreakComplete();
      }
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isRunning, timeLeft]);

  // === PLANNING ===
  const addTask = () => {
    if (tasks.length >= maxPomodori) { toast.error(t("pomodoro.maxReached", `Maximum ${maxPomodori} pomodori (8h)`)); return; }
    setTasks([...tasks, { task_name: "", objective: "" }]);
  };

  const removeTask = (idx) => {
    if (tasks.length <= 1) return;
    setTasks(tasks.filter((_, i) => i !== idx));
  };

  const updateTask = (idx, field, value) => {
    const updated = [...tasks];
    updated[idx] = { ...updated[idx], [field]: value };
    setTasks(updated);
  };

  const createPlan = async () => {
    const validTasks = tasks.filter(t => t.task_name.trim());
    if (validTasks.length === 0) { toast.error(t("pomodoro.enterTask")); return; }
    try {
      const res = await axios.post(`${API}/pomodoro/plan`, {
        tasks: validTasks, work_duration: workDuration,
        break_duration: breakDuration, long_break_duration: longBreakDuration,
      }, { withCredentials: true });
      setPlan(res.data);
      setCurrentIndex(0);
      setCompletedCount(0);
      setTimeLeft(workDuration * 60);
      setPhase("work");
      toast.success(t("pomodoro.planCreated", "Plan cree !"));
    } catch (e) { toast.error(t("pomodoro.errorStarting")); }
  };

  // === TIMER CONTROLS ===
  const startCurrentPomodoro = () => {
    if (!plan || currentIndex >= plan.sessions.length) return;
    setPhase("work");
    setTimeLeft(workDuration * 60);
    setIsRunning(true);
  };

  const toggleTimer = () => setIsRunning(!isRunning);

  const handleWorkComplete = async () => {
    const session = plan.sessions[currentIndex];
    if (!session) return;
    // Mark as completed
    try {
      await axios.put(`${API}/pomodoro/${session.session_id}/complete`, {}, { withCredentials: true });
      if (user?.role === "patient") promptShare({ session_id: session.session_id }, "pomodoro");
    } catch (e) { console.error(e); }
    const newCompleted = completedCount + 1;
    setCompletedCount(newCompleted);
    // Update local plan
    const updatedSessions = [...plan.sessions];
    updatedSessions[currentIndex] = { ...updatedSessions[currentIndex], status: "completed", completed: true };
    setPlan({ ...plan, sessions: updatedSessions });
    // Open review
    setReviewOpen(session.session_id);
    // Determine break type
    const isLongBreak = newCompleted % 4 === 0;
    const bDuration = isLongBreak ? longBreakDuration : breakDuration;
    setPhase(isLongBreak ? "long_break" : "break");
    setTimeLeft(bDuration * 60);
  };

  const handleBreakComplete = () => {
    // Move to next task
    const nextIdx = plan.sessions.findIndex((s, i) => i > currentIndex && (s.status === "pending" || s.status === "deferred"));
    if (nextIdx >= 0) {
      setCurrentIndex(nextIdx);
      setPhase("work");
      setTimeLeft(workDuration * 60);
    } else {
      // All done
      setPhase("work");
      setCurrentIndex(plan.sessions.length);
      toast.success(t("pomodoro.allDone", "Tous les pomodori sont termines !"));
    }
  };

  const startBreak = () => setIsRunning(true);
  const skipBreak = () => { setIsRunning(false); handleBreakComplete(); };

  const submitReview = async () => {
    if (!reviewOpen) return;
    try {
      await axios.put(`${API}/pomodoro/${reviewOpen}`, { review: reviewText, status: "completed" }, { withCredentials: true });
      const updatedSessions = plan.sessions.map(s =>
        s.session_id === reviewOpen ? { ...s, review: reviewText } : s
      );
      setPlan({ ...plan, sessions: updatedSessions });
    } catch (e) { console.error(e); }
    setReviewOpen(null);
    setReviewText("");
    // Auto-start break if not already running
    if (phase === "break" || phase === "long_break") {
      setIsRunning(true);
    }
  };

  const deferTask = async () => {
    if (!plan || currentIndex >= plan.sessions.length) return;
    const session = plan.sessions[currentIndex];
    try {
      await axios.put(`${API}/pomodoro/plan/${plan.plan_id}/defer/${session.session_id}`, {}, { withCredentials: true });
      const updatedSessions = [...plan.sessions];
      updatedSessions[currentIndex] = { ...updatedSessions[currentIndex], status: "deferred" };
      // Move deferred to end
      const deferred = updatedSessions.splice(currentIndex, 1)[0];
      updatedSessions.push(deferred);
      setPlan({ ...plan, sessions: updatedSessions });
      toast.info(t("pomodoro.taskDeferred", "Tache decalee a plus tard"));
      // Find next pending
      const nextIdx = updatedSessions.findIndex(s => s.status === "pending" || s.status === "deferred");
      if (nextIdx >= 0) {
        setCurrentIndex(nextIdx);
        setTimeLeft(workDuration * 60);
      }
    } catch (e) { toast.error("Erreur"); }
    setIsRunning(false);
  };

  const resetPlan = () => {
    setPlan(null);
    setTasks([{ task_name: "", objective: "" }]);
    setCurrentIndex(0);
    setCompletedCount(0);
    setIsRunning(false);
    setPhase("work");
    setTimeLeft(workDuration * 60);
  };

  // === HELPERS ===
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const currentDurationSecs = phase === "work" ? workDuration * 60
    : phase === "long_break" ? longBreakDuration * 60 : breakDuration * 60;
  const progress = currentDurationSecs > 0 ? ((currentDurationSecs - timeLeft) / currentDurationSecs) * 100 : 0;
  const circumference = 2 * Math.PI * 120;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const currentSession = plan?.sessions?.[currentIndex];
  const allDone = plan && currentIndex >= plan.sessions.length;
  const totalEffective = `${Math.floor((completedCount * workDuration) / 60)}h${(completedCount * workDuration) % 60 > 0 ? ((completedCount * workDuration) % 60) + "min" : ""}`;

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="space-y-4"><div className="animate-pulse h-8 w-48 bg-muted rounded" /><div className="animate-pulse h-64 bg-muted rounded-xl" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="pomodoro-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("pomodoro.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("pomodoro.dayPlannerSubtitle", "Planifiez et executez vos pomodori pour la journee")}</p>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.pomodoro} />

        {!plan ? (
          /* === PLANNING PHASE === */
          <div className="space-y-6">
            {/* Settings */}
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Timer className="w-5 h-5" /> {t("pomodoro.settings", "Parametres")}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">{t("pomodoro.workDuration", "Travail (min)")}</Label>
                    <div className="flex gap-1.5">
                      {[15, 25, 45, 60].map(d => (
                        <Button key={d} variant={workDuration === d ? "default" : "outline"} size="sm" onClick={() => setWorkDuration(d)} data-testid={`work-${d}`}>{d}</Button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{t("pomodoro.shortBreak", "Pause courte (min)")}</Label>
                    <div className="flex gap-1.5">
                      {[3, 5, 10].map(d => (
                        <Button key={d} variant={breakDuration === d ? "default" : "outline"} size="sm" onClick={() => setBreakDuration(d)} className={breakDuration === d ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""} data-testid={`break-${d}`}>{d}</Button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{t("pomodoro.longBreak", "Pause longue (min)")}</Label>
                    <div className="flex gap-1.5">
                      {[15, 20, 30].map(d => (
                        <Button key={d} variant={breakDuration === d ? "default" : "outline"} size="sm" onClick={() => setLongBreakDuration(d)} className={longBreakDuration === d ? "bg-blue-600 hover:bg-blue-700 text-white" : ""} data-testid={`long-break-${d}`}>{d}</Button>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{t("pomodoro.longBreakHint", "La pause longue se declenche automatiquement apres 4 pomodori")} | Max {maxPomodori} pomodori (8h)</p>
              </CardContent>
            </Card>

            {/* Task list */}
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><ListTodo className="w-5 h-5" /> {t("pomodoro.planTasks", "Taches de la journee")}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {tasks.map((task, idx) => (
                  <div key={idx} className="flex gap-2 items-start p-3 rounded-lg border bg-muted/30" data-testid={`plan-task-${idx}`}>
                    <span className="text-xs font-bold text-muted-foreground mt-3 w-6 text-center flex-shrink-0">{idx + 1}</span>
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder={t("pomodoro.taskPlaceholder")}
                        value={task.task_name}
                        onChange={(e) => updateTask(idx, "task_name", e.target.value)}
                        data-testid={`task-name-${idx}`}
                      />
                      <Input
                        placeholder={t("pomodoro.objectivePh", "Objectif : que voulez-vous accomplir ?")}
                        value={task.objective}
                        onChange={(e) => updateTask(idx, "objective", e.target.value)}
                        className="text-sm"
                        data-testid={`task-objective-${idx}`}
                      />
                    </div>
                    {tasks.length > 1 && (
                      <Button variant="ghost" size="icon" className="mt-1 text-muted-foreground hover:text-destructive" onClick={() => removeTask(idx)} data-testid={`remove-task-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={addTask} className="gap-1" data-testid="add-task-btn">
                    <Plus className="w-3.5 h-3.5" /> {t("pomodoro.addTask", "Ajouter une tache")}
                  </Button>
                </div>
                <Button onClick={createPlan} className="w-full mt-4 gap-2" size="lg" data-testid="create-plan-btn">
                  <Play className="w-4 h-4" /> {t("pomodoro.startDay", "Demarrer la journee")} ({tasks.filter(t => t.task_name.trim()).length} pomodori)
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* === EXECUTION PHASE === */
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Timer - 3 cols */}
            <div className="lg:col-span-3 space-y-4">
              <Card>
                <CardContent className="p-6 sm:p-8">
                  {/* Timer circle */}
                  <div className="relative w-56 h-56 sm:w-64 sm:h-64 mx-auto mb-6">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="50%" cy="50%" r="120" fill="none" stroke="hsl(var(--muted))" strokeWidth="8" />
                      <circle cx="50%" cy="50%" r="120" fill="none"
                        stroke={phase === "work" ? "hsl(var(--primary))" : phase === "long_break" ? "hsl(217, 91%, 60%)" : "hsl(142, 76%, 36%)"}
                        strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} className="transition-all duration-1000" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      {phase !== "work" && (
                        <span className={`text-xs font-medium mb-1 flex items-center gap-1 ${phase === "long_break" ? "text-blue-600 dark:text-blue-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                          <Coffee className="w-3.5 h-3.5" /> {phase === "long_break" ? t("pomodoro.longBreakLabel", "Pause longue") : "Pause"}
                        </span>
                      )}
                      <span className="text-5xl font-bold font-mono" data-testid="timer-display">{formatTime(timeLeft)}</span>
                      {phase === "work" && currentSession && (
                        <span className="text-sm text-muted-foreground mt-2 max-w-[160px] text-center truncate">{currentSession.task_name}</span>
                      )}
                      <span className="text-xs text-muted-foreground mt-1">{completedCount}/{plan.sessions.length}</span>
                    </div>
                  </div>

                  {/* Review modal */}
                  {reviewOpen && (
                    <Card className="mb-4 border-primary/30 bg-primary/5">
                      <CardContent className="p-4 space-y-3">
                        <p className="text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> {t("pomodoro.reviewTitle", "Bilan du pomodoro")}</p>
                        <Textarea
                          placeholder={t("pomodoro.reviewPh", "Qu'avez-vous accompli ? Comment cela s'est-il passe ?")}
                          value={reviewText} onChange={(e) => setReviewText(e.target.value)}
                          rows={3} data-testid="review-textarea"
                        />
                        <Button size="sm" onClick={submitReview} className="w-full gap-1" data-testid="submit-review-btn">
                          <CheckCircle className="w-3.5 h-3.5" /> {t("pomodoro.submitReview", "Valider et continuer")}
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Controls */}
                  {allDone ? (
                    <div className="text-center space-y-3">
                      <p className="text-lg font-semibold text-primary">{t("pomodoro.allDone", "Journee terminee !")}</p>
                      <p className="text-sm text-muted-foreground">{completedCount} pomodori | {totalEffective} {t("pomodoro.effectiveWork", "de travail effectif")}</p>
                      <Button variant="outline" onClick={resetPlan} data-testid="new-plan-btn">{t("pomodoro.newPlan", "Nouveau plan")}</Button>
                    </div>
                  ) : !isRunning && phase === "work" && !reviewOpen ? (
                    <div className="space-y-2">
                      <Button onClick={startCurrentPomodoro} className="w-full gap-2" size="lg" data-testid="start-pomodoro-btn">
                        <Play className="w-4 h-4" /> {t("pomodoro.startFocus")}
                      </Button>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={deferTask} className="flex-1 gap-1 text-xs" data-testid="defer-task-btn">
                          <SkipForward className="w-3.5 h-3.5" /> {t("pomodoro.deferTask", "Decaler")}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setSoundEnabled(!soundEnabled)} className="gap-1 text-xs">
                          {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>
                  ) : !isRunning && (phase === "break" || phase === "long_break") && !reviewOpen ? (
                    <div className="space-y-2">
                      <Button onClick={startBreak} className={`w-full gap-2 ${phase === "long_break" ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"}`} data-testid="start-break-btn">
                        <Coffee className="w-4 h-4" /> {t("pomodoro.startBreak", "Demarrer la pause")}
                      </Button>
                      <Button variant="outline" size="sm" onClick={skipBreak} className="w-full gap-1 text-xs" data-testid="skip-break-btn">
                        <SkipForward className="w-3.5 h-3.5" /> {t("pomodoro.skipBreak", "Passer la pause")}
                      </Button>
                    </div>
                  ) : !reviewOpen ? (
                    <div className="flex gap-2">
                      <Button onClick={toggleTimer} variant={isRunning ? "outline" : "default"} className="flex-1" data-testid="toggle-timer-btn">
                        {isRunning ? <><Pause className="w-4 h-4 mr-2" /> Pause</> : <><Play className="w-4 h-4 mr-2" /> {t("pomodoro.resume", "Reprendre")}</>}
                      </Button>
                      <Button onClick={() => { setIsRunning(false); setTimeLeft(phase === "work" ? workDuration * 60 : phase === "long_break" ? longBreakDuration * 60 : breakDuration * 60); }} variant="outline" size="icon" data-testid="reset-timer-btn">
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setSoundEnabled(!soundEnabled)}>
                        {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                      </Button>
                    </div>
                  ) : null}

                  {/* Progress bar */}
                  <div className="mt-6 p-3 bg-secondary/50 rounded-lg">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-primary" /> {completedCount}/{plan.sessions.length}</span>
                      <span className="text-muted-foreground">{totalEffective}</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${(completedCount / plan.sessions.length) * 100}%` }} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Task list - 2 cols */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <ListTodo className="w-4 h-4" /> {t("pomodoro.taskList", "Liste des taches")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5 max-h-[600px] overflow-y-auto">
                  {plan.sessions.map((session, idx) => {
                    const isCurrent = idx === currentIndex && !allDone;
                    const isCompleted = session.status === "completed";
                    const isDeferred = session.status === "deferred" && idx !== currentIndex;
                    return (
                      <TaskItem key={session.session_id} session={session} idx={idx} isCurrent={isCurrent} isCompleted={isCompleted} isDeferred={isDeferred} t={t} completedCount={completedCount} />
                    );
                  })}
                </CardContent>
              </Card>
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={resetPlan} className="w-full text-xs gap-1" data-testid="reset-plan-btn">
                  <RotateCcw className="w-3 h-3" /> {t("pomodoro.newPlan", "Nouveau plan")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

const TaskItem = ({ session, idx, isCurrent, isCompleted, isDeferred, t, completedCount }) => {
  const [expanded, setExpanded] = useState(false);
  const showLongBreak = isCompleted && (idx + 1) % 4 === 0;

  return (
    <div data-testid={`task-item-${idx}`}>
      <div
        className={`p-2.5 rounded-lg border text-sm cursor-pointer transition-colors ${
          isCurrent ? "border-primary bg-primary/5 ring-1 ring-primary/20" :
          isCompleted ? "border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800" :
          isDeferred ? "border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800" :
          "border-muted bg-muted/30"
        }`}
        onClick={() => (isCompleted && session.review) && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold border"
            style={{
              backgroundColor: isCompleted ? "hsl(142, 76%, 36%)" : isCurrent ? "hsl(var(--primary))" : "transparent",
              color: isCompleted || isCurrent ? "white" : "hsl(var(--muted-foreground))",
              borderColor: isCompleted ? "hsl(142, 76%, 36%)" : isCurrent ? "hsl(var(--primary))" : "hsl(var(--border))",
            }}>
            {isCompleted ? <CheckCircle className="w-3 h-3" /> : idx + 1}
          </span>
          <div className="flex-1 min-w-0">
            <p className={`font-medium truncate ${isCompleted ? "line-through text-muted-foreground" : ""}`}>{session.task_name}</p>
            {session.objective && <p className="text-xs text-muted-foreground truncate">{session.objective}</p>}
          </div>
          {isCurrent && <ArrowRight className="w-3.5 h-3.5 text-primary flex-shrink-0 animate-pulse" />}
          {isDeferred && <span className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 px-1.5 py-0.5 rounded-full flex-shrink-0">{t("pomodoro.deferred", "Decale")}</span>}
          {isCompleted && session.review && (expanded ? <ChevronUp className="w-3 h-3 text-muted-foreground" /> : <ChevronDown className="w-3 h-3 text-muted-foreground" />)}
        </div>
        {expanded && session.review && (
          <p className="mt-2 text-xs text-muted-foreground border-t pt-2 italic">{session.review}</p>
        )}
      </div>
      {showLongBreak && (
        <div className="flex items-center gap-2 py-1 px-3 text-[10px] text-blue-600 dark:text-blue-400">
          <Coffee className="w-3 h-3" /> {t("pomodoro.longBreakLabel", "Pause longue")}
        </div>
      )}
    </div>
  );
};

export default PomodoroPage;
